import {CommonModule} from '@angular/common';
import {ChangeDetectionStrategy, Component, inject, signal} from '@angular/core';
import {toObservable} from '@angular/core/rxjs-interop';
import {ActivatedRoute, NavigationEnd, Router, RouterOutlet} from '@angular/router';
import {
    composeComparators,
    HiddenValueWhileLoading,
    keyComparator,
    switchMapWithLoading,
    TranslatePipe,
    TranslateService,
} from '@brunata-angular/util';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {Store} from '@ngrx/store';
import {
    combineLatest,
    filter,
    map,
    Observable,
    shareReplay,
    startWith,
    Subject,
    switchMap,
} from 'rxjs';
import {selectBuilding} from 'src/app/state/selectors';
import {EventAlarmsService} from './event-alarms.service';
import {EventAlarmTemplate, EventAlarmWithTranslations} from './event-alarm.model';
import {EventAlarmsCardViewComponent} from './event-alarms-card-view/event-alarms-card-view.component';
import {EventAlarmsTableComponent} from './event-alarms-table/event-alarms-table.component';
import {ConfirmationDialogService} from '@brunata-angular/ui';
import {ToastrService} from 'ngx-toastr';
import {SelectTemplateOrCustomEventAlarmComponent} from './create-event-alarm/select-template-or-custom-event-alarm/select-template-or-custom-event-alarm.component';

type EventAlarmsOverviewView = {
    buildingNo: string;
    eventAlarms: EventAlarmWithTranslations[];
    eventAlarmTemplates: HiddenValueWhileLoading<EventAlarmTemplate[]>;
    noActivatedChildren: boolean;
};

@Component({
    selector: 'app-event-alarms-overview',
    standalone: true,
    imports: [
        CommonModule,
        RouterOutlet,
        TranslatePipe,
        EventAlarmsCardViewComponent,
        EventAlarmsTableComponent,
    ],
    templateUrl: './event-alarms-overview.component.html',
    styleUrl: './event-alarms-overview.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EventAlarmsOverviewComponent {
    private readonly store = inject(Store);
    private readonly activatedRoute = inject(ActivatedRoute);
    private readonly router = inject(Router);
    private readonly eventAlarmsService = inject(EventAlarmsService);
    private readonly translateService = inject(TranslateService);
    private readonly toastrService = inject(ToastrService);
    private readonly modalService = inject(NgbModal);
    private readonly confirmationDialogService = inject(ConfirmationDialogService);

    readonly viewMode = signal<'card' | 'table'>(
        localStorage.getItem(`event-alarm.overview.view-mode`) === 'card' ? 'card' : 'table',
    );

    private readonly updateView$ = new Subject<void>();

    private readonly buildingNo$: Observable<string> = this.store.select(selectBuilding).pipe(
        filter(buildingState => buildingState.status === 'success'),
        map(building => building.buildingNo),
    );

    private readonly eventAlarmTemplates$: Observable<
        HiddenValueWhileLoading<EventAlarmTemplate[]>
    > = this.buildingNo$.pipe(
        switchMapWithLoading(buildingNo =>
            this.eventAlarmsService.getEventTypesForBuilding(buildingNo).pipe(
                map(templates =>
                    templates.map(vendorModel => ({
                        ...vendorModel,
                        typeName: this.translateService.translate(
                            'event-alarms_' + vendorModel.typeName,
                        ),
                        typeDescription: this.translateService.translate(
                            'event-alarms_desc_' + vendorModel.typeName,
                        ),
                    })),
                ),
            ),
        ),
        shareReplay(1),
    );

    private readonly noActivatedChildren$: Observable<boolean> = this.router.events.pipe(
        filter(event => event instanceof NavigationEnd),
        startWith(null),
        map(() => this.activatedRoute.children.length === 0),
    );

    private readonly eventAlarms$: Observable<EventAlarmWithTranslations[]> = combineLatest([
        this.buildingNo$,
        this.noActivatedChildren$.pipe(filter(value => value)),
        this.updateView$.pipe(startWith(null)),
    ]).pipe(
        switchMap(([buildingNo]) =>
            this.eventAlarmsService.getEventAlarms(buildingNo).pipe(
                map(eventAlarms =>
                    eventAlarms.map(eventAlarm => ({
                        ...eventAlarm,
                        usageTranslated: this.translateService.translate(
                            'mappers_allocationUnitMap.' + 'null',
                            'online',
                            'common',
                        ),
                        unitTranslated: this.translateService.translate(
                            'mappers_measurementUnit.' + 'null',
                            'online',
                            'common',
                        ),
                    })),
                ),
            ),
        ),
    );

    private readonly sortedEventAlarm$: Observable<EventAlarmWithTranslations[]> = combineLatest({
        eventAlarms: this.eventAlarms$,
        sorting: this.eventAlarmsService.sorting,
        viewMode: toObservable(this.viewMode),
    }).pipe(
        map(({eventAlarms, sorting, viewMode}) =>
            eventAlarms.sort(
                composeComparators([
                    keyComparator(
                        eventAlarm =>
                            eventAlarm[viewMode === 'card' ? 'alarmName' : sorting.column],
                        viewMode === 'card' ? 'asc' : sorting.direction,
                    ),
                ]),
            ),
        ),
    );

    readonly view$: Observable<EventAlarmsOverviewView> = combineLatest({
        buildingNo: this.buildingNo$,
        eventAlarms: this.sortedEventAlarm$,
        eventAlarmTemplates: this.eventAlarmTemplates$,
        noActivatedChildren: this.noActivatedChildren$,
    });

    selectTemplate(templates: HiddenValueWhileLoading<EventAlarmTemplate[]>): void {
        if (templates.loading) {
            return;
        }
        const modalRef = this.modalService.open(SelectTemplateOrCustomEventAlarmComponent, {
            size: 'xl',
            centered: true,
            backdrop: 'static',
        });

        const component = modalRef.componentInstance as SelectTemplateOrCustomEventAlarmComponent;
        component.templates = templates.value;
        component.templateSelected.subscribe(template => this.eventAlarmTemplateChange(template));
    }

    eventAlarmTemplateChange(template: EventAlarmTemplate): void {
        void this.router.navigate(['create', template.eventTypeId], {
            relativeTo: this.activatedRoute,
        });
    }

    deleteEventAlarm(
        buildingNo: string,
        eventAlarmWithTranslations: EventAlarmWithTranslations,
    ): void {
        void this.confirmationDialogService
            .confirm(
                this.translateService.translate('common_warning', 'online', 'common'),
                this.translateService
                    .translate('monitoring_alarm_delete_warning')
                    .replace('{0}', eventAlarmWithTranslations.alarmName),
            )
            .then(confirmed => {
                if (!confirmed) {
                    return;
                }

                this.eventAlarmsService
                    .deleteEventAlarm(buildingNo, eventAlarmWithTranslations.subscriptionId)
                    .subscribe(() => {
                        this.updateView$.next();
                        this.toastrService.success(
                            this.translateService.translate('event-alarms_delete_success'),
                        );
                    });
            });
    }

    editEventAlarm(subscriptionId: string): void {
        void this.router.navigate([`${subscriptionId}/edit`], {relativeTo: this.activatedRoute});
    }

    duplicateEventAlarm(buildingNo: string, subscriptionId: string): void {
        this.eventAlarmsService
            .getEventAlarm(buildingNo, subscriptionId)
            .pipe(
                switchMap(eventAlarm => {
                    const alarmName = `${eventAlarm.alarmName} - ${this.translateService.translate('utilities_Copy_key')}`;
                    return this.eventAlarmsService
                        .createEventAlarm(buildingNo, {
                            recipients: eventAlarm.recipients,
                            throttle: eventAlarm.throttle,
                            eventTypeId: Number(eventAlarm.eventTypeId),
                            vendorModelIds: eventAlarm.vendorModels.map(
                                model => model.vendorModelId,
                            ),
                            alarmName,
                        })
                        .pipe(map(id => ({id, alarmName})));
                }),
            )
            .subscribe(({id, alarmName}) => {
                void this.router
                    .navigate([`${id}/edit`], {relativeTo: this.activatedRoute})
                    .then(() => {
                        const successMessage = this.translateService
                            .translate('event-alarms_alarm_created')
                            .replace('{0}', alarmName);
                        this.toastrService.success(successMessage);
                    });
            });
    }

    setViewMode(view: 'table' | 'card'): void {
        this.viewMode.set(view);
        localStorage.setItem(`event-alarm.overview.view-mode`, view);
    }
}
